import { useTranslation } from 'react-i18next';
import { LineChart } from '@/shared/components/charts/LineChart';
import { Skeleton } from '@/shared/components/ui/skeleton';
import { useTimeAnalysis } from '../../hooks/useTimeAnalysis';
import { useAllTasks } from '../../hooks/useAllTasks';
import type { ChartTimeseriesData } from '@/shared/components/charts/types';

export function TimeAnalysisSection() {
  const { t } = useTranslation('analytics');
  const { data, isLoading } = useTimeAnalysis();
  const { data: tasksData, isLoading: isLoadingTasks } = useAllTasks({
    sortBy: 'efficiency',
    sortAscending: false,
    pageSize: 10,
  });

  const tasks = tasksData?.items ?? [];
  const maxTracked = Math.max(...tasks.map((task) => task.trackedHours), 1);

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-52 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  const byProjectData: ChartTimeseriesData = {
    labels: (data?.byProject ?? []).map((p) => p.projectName ?? '—'),
    series: [
      {
        name: t('timeAnalysis.estimated'),
        values: (data?.byProject ?? []).map((p) => p.estimatedHours),
        color: '#8b5cf6',
      },
      {
        name: t('timeAnalysis.real'),
        values: (data?.byProject ?? []).map((p) => p.trackedHours),
        color: '#06b6d4',
      },
    ],
  };

  return (
    <div className="space-y-6">
      <div data-testid="analytics-time-by-project">
        <p className="mb-2 text-sm font-semibold">{t('timeAnalysis.byProject')}</p>
        <div className="h-52">
          <LineChart data={byProjectData} />
        </div>
      </div>

      {(isLoadingTasks || tasks.length > 0) && (
        <div data-testid="analytics-time-by-task">
          <p className="mb-3 text-sm font-semibold">{t('timeAnalysis.taskEfficiency')}</p>
          {isLoadingTasks ? (
            <div className="space-y-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              {tasks.map((task) => {
                const barPct = Math.round((task.trackedHours / maxTracked) * 100);
                return (
                  <div key={task.taskId ?? task.title}>
                    <div className="mb-1.5 flex items-center justify-between gap-4">
                      <span className="min-w-0 flex-1 truncate text-sm">{task.title ?? '—'}</span>
                      <div className="flex shrink-0 items-center gap-1.5 text-xs text-muted-foreground">
                        {task.projectColor && (
                          <span
                            className="h-2 w-2 shrink-0 rounded-full"
                            style={{ backgroundColor: task.projectColor }}
                          />
                        )}
                        <span>{task.projectName}</span>
                        <span className="font-bold text-primary">
                          {task.efficiencyPercentage.toFixed(0)}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-secondary">
                      <div
                        className="h-full rounded-full bg-primary transition-all"
                        style={{ width: `${barPct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
